import * as React from 'react';
import {useSize} from '@packages/utilities/styled';
import {HStack} from '../Stack';
import {ToastView, ToastBodyView} from './Toast.styles';
import {ToastButton} from './ToastButton';
import {ToastAction, ToastOptions, ToastType} from './_types';

type ToastBodyProps = Pick<
  ToastOptions,
  'actions' | 'hasIcon' | 'icon' | 'isInline' | 'onClose' | 'title'
> & {
  children: React.ReactNode;
  type: ToastType;
};

/**
 * The visual representation of the Toast component as a whole.
 * Renders the contents of the toast, including the title, body, and actions.
 * It is used internally by the `createToastFacade` function to render the toast.
 * It can be used directly if you want to create a custom toast.
 */
const ToastBody = ({
  actions,
  children,
  // @ts-expect-error - injected prop from react-toastify
  closeToast,
  hasIcon,
  icon,
  isInline = false,
  onClose,
  title,
  type,
  ...rest
}: ToastBodyProps) => {
  const handleClose = () => {
    if (onClose && typeof onClose === 'function') onClose();
    closeToast();
  };

  const size = useSize();

  return (
    <ToastView
      colorVariant={type}
      data-sc={`Toast-${type}`}
      hasCloseButton
      hasIcon={hasIcon}
      hasTitle={Boolean(title)}
      icon={icon}
      isInline={isInline}
      isTinted
      onDismiss={handleClose}
      title={title}
      {...rest}
    >
      <ToastBodyView>{children}</ToastBodyView>
      {actions?.length && (
        <HStack
          isFullWidth={false}
          justify={isInline ? 'end' : 'start'}
          spacing={size === 'x-large' ? 2 : 1}
        >
          {actions.map((action: ToastAction) => (
            <ToastButton
              action={action}
              handleClose={handleClose}
              key={action.text}
              toastVariant={type}
            />
          ))}
        </HStack>
      )}
    </ToastView>
  );
};

export {ToastBody};
